import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Camera, Trash2, Image as ImageIcon, IdCard, ExternalLink, FileText, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import ActiveSessionsCard from "@/components/ActiveSessionsCard";
import MFACard from "@/components/MFACard";
import TimeCard from "@/components/TimeCard";
import StaffAvatar from "@/components/StaffAvatar";
import AvatarCropModal from "@/components/AvatarCropModal";
import CameraCaptureModal from "@/components/CameraCaptureModal";
import DobInput from "@/components/DobInput";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import {
  PasswordChangeCard,
  SignaturePadCard,
  MyDocumentsCard,
  MyAuditLogCard,
  AdminAuditSearchCard,
} from "@/components/prescriber/EmployeeProfileExtras";

/**
 * "My Profile" tab for all staff roles (admin, super_admin, prescriber,
 * customer_service, it, compliance, owner). Lets every employee view and
 * edit their own demographics, contact email, phone, address, emergency
 * contact, and (for prescribers) credentialing fields. Also embeds the
 * employee's personal Time Card, MFA card, and Active Sessions card so
 * they have a single self-service hub.
 */
export default function StaffProfileTab() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // State-license roster (prescribers only). Read-only from this page —
  // admin uploads/edits via Admin → Users → Licenses.
  const [licenses, setLicenses] = useState([]);
  const [form, setForm] = useState({
    name: "",
    role: "",
    npi_number: "",
    dea_number: "",
    license_states: "",
    phone: "",
    personal_email: "",
    fax: "",
    date_of_birth: "",
    gender: "",
    supervising_physician_name: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
  });
  const [npiError, setNpiError] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // bump this on every avatar change so the <img> URL refetches and the
  // browser doesn't show the stale cached photo.
  const [avatarCacheBust, setAvatarCacheBust] = useState(0);
  const fileInputRef = useRef(null);
  // Open the crop modal with a data-URL of the picked file.
  const [cropSrc, setCropSrc] = useState(null);
  // Toggle the live-camera capture modal.
  const [cameraOpen, setCameraOpen] = useState(false);
  const [downloadingCreds, setDownloadingCreds] = useState(false);

  const handleDownloadCredentials = async () => {
    setDownloadingCreds(true);
    try {
      const res = await API.get("/prescribers/me/credentials-pdf", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : "GLPFlo_credentials.pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Credential summary downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download credential summary.");
    } finally {
      setDownloadingCreds(false);
    }
  };

  const reload = async () => {
    const { data } = await API.get("/staff/profile");
    setProfile(data);
    setForm({
      name: data.name || "",
      role: data.role || "",
      npi_number: data.npi_number || "",
      dea_number: data.dea_number || "",
      license_states: Array.isArray(data.license_states) ? data.license_states.join(", ") : (data.license_states || ""),
      phone: data.phone || "",
      personal_email: data.personal_email || "",
      fax: data.fax || "",
      date_of_birth: data.date_of_birth || "",
      gender: data.gender || "",
      supervising_physician_name: data.supervising_physician_name || "",
      address_street: data.address_street || "",
      address_city: data.address_city || "",
      address_state: data.address_state || "",
      address_zip: data.address_zip || "",
      emergency_contact_name: data.emergency_contact_name || "",
      emergency_contact_phone: data.emergency_contact_phone || "",
      emergency_contact_relationship: data.emergency_contact_relationship || "",
    });
    // Prescribers get their state-license roster loaded read-only so
    // they can see (and open) each license without needing admin help.
    if (data.role === "prescriber" && data.id) {
      try {
        const { data: lic } = await API.get(`/prescribers/${data.id}/licenses`);
        setLicenses(lic.licenses || []);
      } catch { /* silent — not blocking the rest of the page */ }
    }
  };

  useEffect(() => {
    reload().catch(() => {}).finally(() => setLoading(false));
  }, []);

  const validateNpi = (npi) => {
    if (!npi) { setNpiError(""); return true; }
    if (!/^\d{10}$/.test(npi)) { setNpiError("NPI must be exactly 10 digits"); return false; }
    const full = "80840" + npi;
    let total = 0;
    for (let i = full.length - 1, alt = false; i >= 0; i--, alt = !alt) {
      let d = parseInt(full[i]);
      if (alt) { d *= 2; if (d > 9) d -= 9; }
      total += d;
    }
    if (total % 10 !== 0) { setNpiError("Invalid NPI — check digit failed"); return false; }
    setNpiError("");
    return true;
  };

  const handleSave = async () => {
    if (!validateNpi(form.npi_number)) return;
    setSaving(true);
    try {
      // Strip prescriber-only fields when the user isn't a prescriber so we
      // never accidentally clear them server-side.
      const payload = { ...form };
      if (!isPrescriber) {
        delete payload.npi_number;
        delete payload.dea_number;
        delete payload.supervising_physician_name;
      } else {
        // Prescribers CANNOT self-edit licensure fields (NPI, DEA,
        // license numbers, DOB/gender) — LegitScript. Practice address,
        // phone, and fax ARE self-editable since those change whenever
        // the prescriber opens a new office.
        [
          "npi_number", "dea_number", "license_number", "license_states",
          "date_of_birth", "gender",
          "supervising_physician_name",
          "address_street", "address_city", "address_state", "address_zip",
        ].forEach((k) => { delete payload[k]; });
      }
      await API.put("/staff/profile", payload);
      toast.success("Profile updated");
      // Refresh so the completion % bar reflects newly-filled fields.
      reload().catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Photo must be JPG, PNG, or WebP");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Photo too large — max 4 MB");
      return;
    }
    // Read into a data URL so react-easy-crop has a stable source even after
    // the <input type=file> is cleared.
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target?.result || null);
    reader.onerror = () => toast.error("Could not read photo");
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadCroppedAvatar = async (blob) => {
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      // Backend keys off content-type; force PNG since the canvas exports PNG.
      fd.append("file", blob, "avatar.png");
      await API.post("/staff/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile photo updated");
      setAvatarCacheBust((n) => n + 1);
      setCropSrc(null);
      reload().catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!profile?.avatar_url) return;
    if (!(await brandedConfirm({ title: 'Delete?', description: "Remove your profile photo?", confirmText: 'Delete', cancelText: 'Cancel', tone: 'danger' }))) return;
    try {
      await API.delete("/staff/profile/avatar");
      toast.success("Profile photo removed");
      setAvatarCacheBust((n) => n + 1);
      reload().catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove photo");
    }
  };

  const isPrescriber = profile?.role === "prescriber";
  const isNp = isPrescriber && profile?.prescriber_type === "np";
  const roleLabel = (profile?.role || "").replace("_", " ");
  const isSuperAdmin = !!profile?.is_super_admin || profile?.role === "super_admin";
  // Peer roles a non-super-admin user can self-assign WHEN can_change_own_role=true.
  const PEER_ROLES = ["customer_service", "it", "compliance"];
  const canChangeOwnRole = !!profile?.can_change_own_role && PEER_ROLES.includes(profile?.role);
  const roleEditable = isSuperAdmin || canChangeOwnRole;
  // Available options for the role <select> depend on who's editing.
  const availableRoles = isSuperAdmin
    ? ["admin", "prescriber", "customer_service", "it", "compliance", "owner"]
    : PEER_ROLES;

  if (loading) return <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading...</div>;

  return (
    <div data-testid="staff-profile-tab">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>My Profile</h1>
      <p className="text-slate-400 dark:text-slate-500 text-sm mb-4">
        {isPrescriber
          ? "Your prescriber credentials and state licenses are locked and managed by an admin. Practice address, phone, and emergency contact are self-serviceable."
          : "Manage your contact info, demographics, address, and emergency contact. Used by HR, payroll, and the time-clock system."}
      </p>

      {/* Prescriber-only lockdown notice + link to license roster. */}
      {isPrescriber && (
        <div
          className="max-w-4xl mb-6 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 flex items-start gap-3"
          data-testid="prescriber-lockdown-banner"
        >
          <IdCard className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-bold text-amber-900 dark:text-amber-200">Prescriber demographics are admin-managed</p>
            <p className="text-amber-800 dark:text-amber-300 mt-0.5">
              For LegitScript and state medical board compliance, only an admin can update your NPI, DEA, license numbers, state licenses, or practice address. Your state licenses and practice address are viewable below — contact admin@glpflo.org to add, renew, or change any of them.
            </p>
          </div>
        </div>
      )}

      {/* Prescriber-only: read-only state licenses roster. Add / renew via
          Admin → Users → Licenses (this table is a mirror). */}
      {isPrescriber && (
        <div className="max-w-4xl mb-6" data-testid="prescriber-licenses-panel">
          <div className="flex items-end justify-between mb-2 gap-2 flex-wrap">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <IdCard className="w-3.5 h-3.5" /> Your state licenses ({licenses.length})
            </p>
            <button
              type="button"
              onClick={handleDownloadCredentials}
              disabled={downloadingCreds}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-full px-3 py-1.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="download-credentials-btn"
              title="One-page PDF: NPI, DEA, practice address, every state license. Hand to credentialing or payer rosters."
            >
              {downloadingCreds ? (
                <>
                  <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                  Preparing…
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5" />
                  Download credential summary
                </>
              )}
            </button>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
            {licenses.length === 0 ? (
              <p className="p-4 text-sm text-slate-500 text-center">
                No state licenses on file yet. Contact admin@glpflo.org to add one — orders can't be routed to you until at least one active license is on record.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-800 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">State</th>
                    <th className="px-3 py-2 text-left">License #</th>
                    <th className="px-3 py-2 text-left hidden md:table-cell">NPI</th>
                    <th className="px-3 py-2 text-left">Expires</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Scan</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((l) => (
                    <tr key={l.id} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-3 py-2 font-bold">{l.state}</td>
                      <td className="px-3 py-2 font-mono text-xs">{l.license_number}</td>
                      <td className="px-3 py-2 font-mono text-xs hidden md:table-cell">{l.npi_number || "—"}</td>
                      <td className="px-3 py-2">{l.expiration_date}</td>
                      <td className="px-3 py-2">
                        {!l.enabled ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">Disabled</span>
                        ) : l.is_active ? (
                          l.expiring_soon ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 rounded-full px-2 py-0.5"><AlertTriangle className="w-3 h-3" /> Expiring soon</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Active</span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-400 rounded-full px-2 py-0.5"><AlertTriangle className="w-3 h-3" /> Expired</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {l.file_url ? (
                          <a href={l.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline">
                            <FileText className="w-3 h-3" /> View <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : <span className="text-xs text-slate-400">Not uploaded</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      <div className="max-w-4xl space-y-6">
        {/* Avatar + completion header */}
        <div className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/10 dark:to-slate-900 rounded-xl p-5 border border-purple-200 dark:border-purple-900/40 flex items-center gap-5" data-testid="profile-header-card">
          <div className="relative shrink-0">
            <StaffAvatar
              src={profile?.avatar_url ? `${profile.avatar_url}?v=${avatarCacheBust}` : ""}
              name={profile?.name}
              size="xl"
              data-testid="profile-avatar"
            />
            {/* Two quick-action buttons stacked on the avatar — Upload (file)
                + Selfie (live camera). Both feed the same crop modal. */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              title="Upload a photo file"
              className="absolute -bottom-1 -right-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-full p-1.5 shadow-md border-2 border-white dark:border-slate-900"
              data-testid="upload-avatar-btn"
            >
              <ImageIcon className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              disabled={uploadingAvatar}
              title="Take a selfie with your camera"
              className="absolute -top-1 -right-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-full p-1.5 shadow-md border-2 border-white dark:border-slate-900"
              data-testid="camera-avatar-btn"
            >
              <Camera className="w-3.5 h-3.5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleAvatarPick}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Profile completeness</p>
              <span className="text-sm font-bold text-purple-600" data-testid="completion-percent">{profile?.completion?.percent || 0}%</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-purple-100 dark:bg-purple-900/30 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-700 transition-all"
                style={{ width: `${profile?.completion?.percent || 0}%` }}
                data-testid="completion-bar"
              />
            </div>
            {(profile?.completion?.missing?.length || 0) > 0 ? (
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400" data-testid="completion-missing">
                Still needed: <span className="font-medium text-slate-700 dark:text-slate-200">{profile.completion.missing.join(" · ")}</span>
              </p>
            ) : (
              <p className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">All set — your profile is fully complete.</p>
            )}
            {profile?.avatar_url && (
              <button
                type="button"
                onClick={handleAvatarRemove}
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400"
                data-testid="remove-avatar-btn"
              >
                <Trash2 className="w-3 h-3" /> Remove photo
              </button>
            )}
          </div>
        </div>

        {/* Editable identity card — name + role (gated) + read-only employee ID */}
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-5 border border-slate-300 dark:border-slate-700">
          <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-4">Identity</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Display Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300"
                data-testid="staff-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Employee ID <span className="text-slate-500 dark:text-slate-400 normal-case">(read-only)</span></Label>
                <Input
                  value={profile?.employee_id || ""}
                  readOnly
                  className="mt-1 rounded-lg bg-slate-200 dark:bg-slate-950 border-slate-600 text-slate-600 dark:text-slate-400 font-mono cursor-not-allowed"
                  data-testid="profile-emp-id"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  Role
                  {!roleEditable && (
                    <span title='Locked — ask an admin to enable role changes' className="text-amber-500 cursor-help" data-testid="role-locked-badge">🔒</span>
                  )}
                </Label>
                {roleEditable ? (
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="mt-1 w-full h-10 rounded-lg bg-white dark:bg-slate-900 border border-slate-600 text-slate-900 dark:text-white px-3 text-sm capitalize"
                    data-testid="staff-role-select"
                  >
                    {availableRoles.map((r) => (
                      <option key={r} value={r}>{r.replace("_", " ")}</option>
                    ))}
                  </select>
                ) : (
                  <div
                    className="mt-1 h-10 rounded-lg bg-slate-200 dark:bg-slate-950 border border-slate-600 px-3 text-sm text-slate-600 dark:text-slate-400 flex items-center capitalize cursor-not-allowed"
                    title="Locked — ask an admin to enable role changes"
                    data-testid="staff-role-locked"
                  >
                    {roleLabel || "—"}
                  </div>
                )}
              </div>
            </div>
            {profile?.prescriber_type && (
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Prescriber Type</Label>
                <p className="mt-1 text-sm text-slate-900 dark:text-white uppercase">{profile.prescriber_type}</p>
              </div>
            )}
          </div>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-3">
            {isSuperAdmin
              ? "Super-admin: you can edit every field on this page except Employee ID."
              : roleEditable
                ? "Your admin has granted you self-role-change between Customer Service, IT, and Compliance."
                : "Role changes are managed by your admin. Ask them to enable self-role-change if you need to switch lanes."}
          </p>
        </div>

        {/* Contact + Demographics */}
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-5 border border-slate-300 dark:border-slate-700">
          <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-4">Contact &amp; Demographics</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Personal / Login Email</Label>
              <Input type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} placeholder="you@example.com" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-personal-email" />
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">This is also your login email. Must be unique across GLPFlo.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Phone Number</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-phone-input" />
              </div>
              {isPrescriber && (
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Fax</Label>
                  <Input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} placeholder="(555) 123-4568" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-fax-input" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Date of Birth</Label>
                <DobInput value={form.date_of_birth} onChange={(iso) => setForm({ ...form, date_of_birth: iso })} className="mt-1" data-testid="staff-dob-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Gender</Label>
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-white dark:bg-slate-900 border border-slate-600 text-slate-900 dark:text-white px-3 text-sm" data-testid="staff-gender-input">
                  <option value="">Prefer not to say</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="nonbinary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Prescriber-only credentials */}
        {isPrescriber && (
          <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-5 border border-slate-300 dark:border-slate-700">
            <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-4">Prescriber Credentials</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">NPI Number</Label>
                <Input value={form.npi_number} onChange={(e) => { setForm({ ...form, npi_number: e.target.value }); if (npiError) validateNpi(e.target.value); }} placeholder="e.g. 1234567890" className={`mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300 ${npiError ? "border-red-500" : ""}`} data-testid="staff-npi-input" />
                {npiError ? <p className="text-[10px] text-red-400 mt-1">{npiError}</p> : <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">10-digit National Provider Identifier — validated with Luhn check digit. Auto-loads onto ERX.</p>}
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">DEA Number <span className="normal-case text-slate-400">(optional)</span></Label>
                <Input value={form.dea_number} onChange={(e) => setForm({ ...form, dea_number: e.target.value })} placeholder="Not required" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-dea-input" />
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Never required — GLPFlo only prescribes non-controlled compounded GLP-1s.</p>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">License State(s)</Label>
                <Input value={form.license_states} onChange={(e) => setForm({ ...form, license_states: e.target.value })} placeholder="e.g. TX, FL, CA" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-license-states-input" />
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Two-letter state codes where you&apos;re licensed, comma-separated. Sent to the pharmacy on every order — required or the pharmacy rejects the script.</p>
              </div>
              {isNp && (
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Supervising Physician</Label>
                  <Input value={form.supervising_physician_name} onChange={(e) => setForm({ ...form, supervising_physician_name: e.target.value })} placeholder="Dr. Full Name" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-sup-physician-input" />
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Required for NP prescribers — auto-loads onto ERX.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Address */}
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-5 border border-slate-300 dark:border-slate-700" data-testid="staff-address-card">
          <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            {isPrescriber ? "Practice Address" : "Home / Mailing Address"}
            {isPrescriber && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-800 rounded-full px-2 py-0.5 normal-case tracking-normal" data-testid="address-locked-badge">
                <IdCard className="w-3 h-3" /> Admin-managed
              </span>
            )}
          </h3>
          {isPrescriber && (
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-4">
              This address auto-loads onto your ERX prescriptions. For LegitScript / state medical board recordkeeping it&apos;s managed by your admin — contact admin@glpflo.org to update it.
            </p>
          )}
          {isPrescriber ? (
            <div className="space-y-3" data-testid="prescriber-address-readonly">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Street Address</Label>
                <div className="mt-1 h-10 rounded-lg bg-slate-200 dark:bg-slate-950 border border-slate-600 px-3 text-sm text-slate-700 dark:text-slate-200 flex items-center" data-testid="staff-address-street-ro">
                  {form.address_street || <span className="text-slate-400 italic">Not set — ask admin</span>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">City</Label>
                  <div className="mt-1 h-10 rounded-lg bg-slate-200 dark:bg-slate-950 border border-slate-600 px-3 text-sm text-slate-700 dark:text-slate-200 flex items-center" data-testid="staff-address-city-ro">
                    {form.address_city || <span className="text-slate-400 italic">—</span>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">State</Label>
                  <div className="mt-1 h-10 rounded-lg bg-slate-200 dark:bg-slate-950 border border-slate-600 px-3 text-sm text-slate-700 dark:text-slate-200 flex items-center uppercase" data-testid="staff-address-state-ro">
                    {form.address_state || <span className="text-slate-400 italic normal-case">—</span>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">ZIP</Label>
                  <div className="mt-1 h-10 rounded-lg bg-slate-200 dark:bg-slate-950 border border-slate-600 px-3 text-sm text-slate-700 dark:text-slate-200 flex items-center" data-testid="staff-address-zip-ro">
                    {form.address_zip || <span className="text-slate-400 italic">—</span>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Street Address</Label>
                <AddressAutocomplete
                  value={form.address_street}
                  onChange={(val) => setForm({ ...form, address_street: val })}
                  onSelect={(s) => setForm({
                    ...form,
                    address_street: s.street || form.address_street,
                    address_city: s.city || form.address_city,
                    address_state: s.state || form.address_state,
                    address_zip: s.zip || form.address_zip,
                  })}
                  placeholder="123 Main Street"
                  testId="staff-address-street"
                  className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">City</Label>
                  <Input value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} placeholder="Tampa" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-address-city" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">State</Label>
                  <Input value={form.address_state} onChange={(e) => setForm({ ...form, address_state: e.target.value.toUpperCase() })} placeholder="FL" maxLength={2} className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300 uppercase" data-testid="staff-address-state" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">ZIP</Label>
                  <Input value={form.address_zip} onChange={(e) => setForm({ ...form, address_zip: e.target.value })} placeholder="33601" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-address-zip" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Emergency Contact */}
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-5 border border-slate-300 dark:border-slate-700">
          <h3 className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-4">Emergency Contact</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Contact Name</Label>
                <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="Jane Doe" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-ec-name" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Relationship</Label>
                <Input value={form.emergency_contact_relationship} onChange={(e) => setForm({ ...form, emergency_contact_relationship: e.target.value })} placeholder="Spouse / Parent / Sibling" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-ec-rel" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Contact Phone</Label>
              <Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} placeholder="(555) 987-6543" className="mt-1 rounded-lg bg-white dark:bg-slate-900 border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-600 dark:text-slate-300" data-testid="staff-ec-phone" />
            </div>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving || !!npiError} className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-staff-profile-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Profile"}
        </Button>
      </div>

      <div className="mt-8 max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TimeCard />
        <MFACard />
        <ActiveSessionsCard />
        <PasswordChangeCard />
        <SignaturePadCard />
        <MyDocumentsCard />
        <MyAuditLogCard />
        {profile?.role === "admin" && <AdminAuditSearchCard />}
      </div>

      <AvatarCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        busy={uploadingAvatar}
        onClose={() => setCropSrc(null)}
        onSave={uploadCroppedAvatar}
      />

      <CameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(dataUrl) => {
          setCameraOpen(false);
          // Hand off into the existing crop modal so the user can fine-tune.
          setCropSrc(dataUrl);
        }}
      />
    </div>
  );
}
